import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from 'Button';
import { Modal } from 'Modal';
import { Text } from 'Text';
import { parseHtml } from 'FrontRoyalAngular/html.utils';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { type BrowseStream, type StreamAccess } from '../BrowseCourses.types';
import '../locales/browseCourses/browseCourses-en.json';

type Props = {
    stream: BrowseStream;
    access: StreamAccess;
    onNotify: (stream: BrowseStream) => void;
    onClose: () => void;
};

// Explains why a course cannot launch. A coming-soon course offers to notify the user.
// The ContentAccessHelper reason text can hold links, so it renders as HTML.
export const CourseUnavailableModal: FC<Props> = ({ stream, access, onNotify, onClose }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });
    const { brandNameShort } = useTargetBrandConfig();

    if (access.reason !== 'coming_soon') {
        return (
            <Modal title={access.reasonTitle} size="md" onClose={onClose}>
                <Text as="div" variant="p" className="px-7.5 pb-7.5">
                    {parseHtml(access.reasonMessage ?? '')}
                </Text>
            </Modal>
        );
    }

    return (
        <Modal title={t('coming_soon')} size="md" onClose={onClose}>
            {({ closeModal }) => (
                <div className="flex flex-col gap-6 px-7.5 pb-7.5">
                    <Text as="p">
                        <Trans
                            t={t}
                            i18nKey="comingSoonModal"
                            values={{ brandName: brandNameShort, title: stream.title }}
                            components={{ strong: <strong />, br: <br /> }}
                        />
                    </Text>
                    <div className="flex justify-end gap-3">
                        <Button variant="bordered" color="slate-grey" height={9} onPress={closeModal}>
                            {t('cancel')}
                        </Button>
                        <Button
                            color="primary"
                            height={9}
                            onPress={() => {
                                onNotify(stream);
                                closeModal();
                            }}
                        >
                            {t('notify')}
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
